import { DisparityField } from 'libs/end-to-end/src/progress';
import { z } from 'zod';

export const ExplanationsJudgeOutput = z.object({
  score: z
    .number()
    .describe(
      'Score from 1 (none correct) to 10 (all correct) based on how well the LLM output reflects JSON-provided probabilities or sentence templates.',
    ),
  rationale: z
    .string()
    .describe(
      'Rationale that explains why this score was given, focused only on percentage accuracy or template adherence.',
    ),
});

export type ExplanationsJudgeOutput = z.infer<typeof ExplanationsJudgeOutput>;

// Mode in python: sf_breakdown
export const ExplanationsSuccessFactorsBreakdownOutput = z.object({
  overview: z.string().describe('Overview of the most optimal feature rule'),
  description: z
    .string()
    .describe('Description of the remaining feature rules'),
});

export type ExplanationsSuccessFactorsBreakdownOutput = z.infer<
  typeof ExplanationsSuccessFactorsBreakdownOutput
>;

// Mode in python: sf_rule
export const ExplanationsSuccessFactorsRuleOutput = z.object({
  overview: z.string().describe('Overview of the most optimal feature rule'),
});

export type ExplanationsSuccessFactorsRuleOutput = z.infer<
  typeof ExplanationsSuccessFactorsRuleOutput
>;

// Mode in python: sf_example
export const ExplanationsSuccessFactorsExampleOutput = z.object({
  overview: z
    .string()
    .describe(
      'Example scenario between two people describing the feature rules',
    ),
});

export type ExplanationsSuccessFactorsExampleOutput = z.infer<
  typeof ExplanationsSuccessFactorsExampleOutput
>;

// Mode in python: sf_optimal_rule
export const ExplanationsSuccessFactorsOptimalRuleOutput = z.object({
  overview: z
    .string()
    .describe('Single sentence that describes the most optimal feature'),
});

export type ExplanationsSuccessFactorsOptimalRuleOutput = z.infer<
  typeof ExplanationsSuccessFactorsOptimalRuleOutput
>;

// Mode in python: hm_overview
export const ExplanationsHeatmapOverviewOutput = z.object({
  overview: z.string().describe('Overview of the filters and their metrics'),
  description: z
    .string()
    .describe('Description of each of the filters and their metrics'),
});

export type ExplanationsHeatmapOverviewOutput = z.infer<
  typeof ExplanationsHeatmapOverviewOutput
>;

// Mode in python: pr_overview or tr_overview
export const ExplanationsProgressOverviewOutput = z.object({
  overview: z
    .string()
    .describe('Overview of the key group differences across years'),
  positive_description: z
    .string()
    .describe(
      'Description of those groups that increase or remained stable in metric over time',
    ),
  negative_description: z
    .string()
    .describe('Description of those groups that decrease in metric over time'),
});

export type ExplanationsProgressOverviewOutput = z.infer<
  typeof ExplanationsProgressOverviewOutput
>;

export const getHeatmapOverviewExplanationOutputKey = ({
  group,
}: {
  group: string;
}) => `hm_overview/${group}`;

export const getProgressOverviewExplanationOutputKey = ({
  disparity,
}: {
  disparity: DisparityField;
}) => `pr_overview/${disparity}`;

export const getKeyFactorsBreakdownOutputKey = ({
  traitName,
}: {
  traitName: string;
}) => `sf_breakdown/${traitName}`;

export const getKeyFactorsExampleOutputKey = ({
  traitName,
}: {
  traitName: string;
}) => `sf_example/${traitName}`;

export const getKeyFactorsOptimalRuleOutputKey = ({
  traitName,
}: {
  traitName: string;
}) => `sf_optimal_rule/${traitName}`;

export const getKeyFactorsRuleOutputKey = ({
  traitName,
  levelIndex,
}: {
  traitName: string;
  levelIndex: number;
}) => `sf_rule/${traitName}/${levelIndex}`;
